/**
 * PyramidMappingConsolePage — admin console for the Pyramid → Cloverleaf enum
 * mappings. Surfaces every Pyramid aggregation / data-type code that an import
 * has met but that is still unmapped, so an admin maps it ONCE here and every
 * future import reuses it. Nothing is ever defaulted silently.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Pyramid, AlertTriangle, CheckCircle2, Save, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';

const BUCKET_LABEL = { aggregations: 'Aggregation', dataTypes: 'Data type' };

function Tile({ label, value, tone, testid }) {
  return (
    <div className="glass-card rounded-2xl px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-xl font-heading font-bold ${tone || 'text-foreground'}`}>{value}</div>
    </div>
  );
}

export default function PyramidMappingConsolePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [codes, md] = await Promise.all([
        api.pyrModelUnmappedCodes(),
        api.pyrModelMappingDefaults(),
      ]);
      setData(codes);
      setDefaults(md);
      setDraft({});
    } catch (err) {
      toast.error(err.message || 'Could not load the Pyramid mapping console');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const choicesFor = (bucket) => (bucket === 'aggregations'
    ? (data?.aggregationCandidates || []).map((c) => ({ value: c.cloverleaf, label: c.label }))
    : (data?.cloverleafDataTypes || []).map((t) => ({ value: t, label: t })));

  const save = async () => {
    const entries = Object.entries(draft).filter(([, v]) => v);
    if (!entries.length) { toast.error('Pick a Cloverleaf target for at least one code first.'); return; }
    setSaving(true);
    try {
      const overrides = { aggregations: {}, dataTypes: {} };
      for (const [key, value] of entries) {
        const [bucket, code] = key.split('::');
        overrides[bucket][code] = { normalized: String(value).toUpperCase(), cloverleaf: value };
      }
      await api.pyrModelSaveMappingDefaults({ mapping_overrides: overrides });
      toast.success(`Saved ${entries.length} mapping(s) — every future Pyramid import will reuse them`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not save the mappings');
    } finally {
      setSaving(false);
    }
  };

  const removeMapping = async (bucket, code) => {
    try {
      await api.pyrModelSaveMappingDefaults({ mapping_overrides: { [bucket]: { [code]: null } } });
      toast.success(`Removed the mapping for ${code}`);
      await load();
    } catch (err) { toast.error(err.message || 'Could not remove the mapping'); }
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="pyr-console-loading"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>
    );
  }

  const codes = data?.codes || [];
  const unmapped = codes.filter((c) => !c.mapped);
  const savedBuckets = defaults?.overrides || {};
  const savedRows = ['aggregations', 'dataTypes'].flatMap((bucket) =>
    Object.entries(savedBuckets[bucket] || {}).map(([code, v]) => ({ bucket, code, ...v })));

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="pyramid-mapping-console-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
          <Pyramid className="w-6 h-6 text-amber-400" /> Pyramid Mapping Console
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pyramid codes that the importer met but could not translate. Map a code once here and
          every future Pyramid import reuses it — unknown codes are never defaulted to SUM.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Awaiting a mapping" value={unmapped.length} tone={unmapped.length ? 'text-rose-400' : 'text-emerald-400'} testid="pyr-console-unmapped-count" />
        <Tile label="Saved mappings" value={savedRows.length} tone="text-sky-400" testid="pyr-console-saved-count" />
        <Tile label="Batches scanned" value={data?.batchesScanned ?? 0} testid="pyr-console-batches" />
        <Tile label="Registry" value={data?.registryVersion || '—'} testid="pyr-console-registry" />
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-xl" onClick={load} data-testid="pyr-console-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Rescan imports
        </Button>
        <Button size="sm" className="rounded-xl" onClick={save} disabled={saving} data-testid="pyr-console-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save mappings
        </Button>
      </div>

      {unmapped.length === 0 ? (
        <div className="glass-card rounded-2xl p-8 text-center text-sm text-muted-foreground" data-testid="pyr-console-empty">
          <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
          Nothing is waiting on you — every Pyramid code seen so far is mapped.
        </div>
      ) : (
        <div className="space-y-2" data-testid="pyr-console-unmapped-list">
          {unmapped.map((c) => {
            const key = `${c.bucket}::${c.sourceCode}`;
            return (
              <div key={key} className="glass-card rounded-2xl p-4 border-rose-500/30 space-y-2" data-testid={`pyr-console-code-${c.bucket}-${c.sourceCode}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertTriangle className="w-4 h-4 text-rose-400" />
                  <Badge variant="outline" className="text-[9px] border-border/50">{BUCKET_LABEL[c.bucket]}</Badge>
                  <span className="text-sm font-semibold text-foreground font-mono">{c.sourceCode}</span>
                  <span className="text-xs text-muted-foreground">
                    {c.affectedCount} object(s) across {c.batchCount} import(s)
                    {c.lastSeenModel ? ` · last seen in ${c.lastSeenModel}` : ''}
                  </span>
                </div>
                {(c.examples || []).length > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    e.g. {(c.examples || []).map((e) => e.displayName || e.name || String(e)).slice(0, 4).join(', ')}
                  </p>
                )}
                <Select value={draft[key] || ''} onValueChange={(v) => setDraft((d) => ({ ...d, [key]: v }))}>
                  <SelectTrigger className="rounded-xl h-9 text-xs max-w-sm" data-testid={`pyr-console-select-${c.sourceCode}`}>
                    <SelectValue placeholder={`Map to a Cloverleaf ${BUCKET_LABEL[c.bucket].toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {choicesFor(c.bucket).map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-base font-heading font-semibold text-foreground">Saved mappings</h2>
        {savedRows.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="pyr-console-no-saved">No mappings saved for this workspace yet.</p>
        ) : (
          <div className="glass-card rounded-2xl overflow-hidden" data-testid="pyr-console-saved-list">
            <table className="w-full text-xs">
              <thead className="bg-card/60"><tr>
                {['Kind', 'Pyramid code', 'Cloverleaf target', 'Mapped by', 'When', ''].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>))}
              </tr></thead>
              <tbody>
                {savedRows.map((r) => (
                  <tr key={`${r.bucket}-${r.code}`} className="border-t border-border/20" data-testid={`pyr-console-saved-${r.bucket}-${r.code}`}>
                    <td className="px-3 py-2 text-muted-foreground">{BUCKET_LABEL[r.bucket]}</td>
                    <td className="px-3 py-2 font-mono text-foreground">{r.code}</td>
                    <td className="px-3 py-2 text-emerald-300">{r.cloverleaf || r.normalized}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.mapped_by_name || r.mapped_by || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">{(r.mapped_at || '').slice(0, 10) || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" className="h-7 text-rose-400" onClick={() => removeMapping(r.bucket, r.code)} data-testid={`pyr-console-remove-${r.code}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
